import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { resolveLocale, translate } from '~/i18n/translate'
import type { Locale } from '~/i18n/resources'
import { getTextDirection } from '~/i18n/server'

interface Props {
  title: string
  children: React.ReactNode
}

function detectPortal(pathname: string): 'provider' | 'backoffice' | 'client' {
  if (pathname.startsWith('/provider')) return 'provider'
  if (pathname.startsWith('/admin') || pathname.startsWith('/backoffice')) return 'backoffice'
  return 'client'
}

function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return resolveLocale(null)
  return resolveLocale(navigator.language)
}

export function PortalLayout({ title, children }: Props) {
  const { pathname } = useLocation()
  const portal = detectPortal(pathname)
  const locale = detectLocale()
  const dir = getTextDirection(locale)

  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('theme-provider', 'theme-backoffice', 'theme-client')
    root.classList.add(`theme-${portal}`)
    root.dataset.portal = portal
    root.lang = locale
    root.dir = dir
  }, [portal, locale, dir])

  useEffect(() => {
    const suffix = translate(locale, 'ui.nav.procurementPortal')
    document.title = `${title} | ${suffix}`
  }, [title, locale])

  return <>{children}</>
}
